/**
* TelemetryContext.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { Application } from './context/Application';
import { User } from './context/User';
import { Web } from './context/Web';
import { OperatingSystem } from './context/OperatingSystem';
import { IntWeb } from './context/IntWeb';
import { Utc } from './context/Utc';
import { Loc } from './context/Loc';
import { Device } from './context/Device';
import { Session } from './context/Session';
import { AppExtensionKeys, UserExtensionKeys, WebExtensionKeys, SDKExtKeys, OSExtKeys, IntWebExtKeys, UtcExtKeys, LocExtKeys, SessionExtKeys, DeviceExtKeys } from './ExtensionKeys';
import { Extensions } from './Extensions';
import { Sdk } from './context/Sdk';
import { SessionManager } from './SessionManager';
var TelemetryContext = /** @class */ (function () {
    function TelemetryContext(coreConfig, propertiesConfig, core) {
        this.app = new Application(propertiesConfig);
        this.user = new User(coreConfig);
        this.os = new OperatingSystem(propertiesConfig);
        this.web = new Web(propertiesConfig);
        this.sdk = new Sdk(coreConfig);
        this.intWeb = new IntWeb(propertiesConfig);
        this.utc = new Utc();
        this.loc = new Loc();
        this.device = new Device();
        this.sessionManager = new SessionManager(propertiesConfig, core && core.logger);
        this.session = new Session();
    }
    TelemetryContext.prototype.applyApplicationContext = function (event) {
        if (typeof this.app.id === 'string') {
            event.ext[Extensions.AppExt][AppExtensionKeys.id] = this.app.id;
        }
        if (typeof this.app.ver === 'string') {
            event.ext[Extensions.AppExt][AppExtensionKeys.ver] = this.app.ver;
        }
        if (typeof this.app.name === 'string') {
            event.ext[Extensions.AppExt][AppExtensionKeys.appName] = this.app.name;
        }
        if (typeof this.app.locale === 'string') {
            event.ext[Extensions.AppExt][AppExtensionKeys.locale] = this.app.locale;
        }
        var expId = this.app.expId;
        if (typeof expId === 'string') {
            event.ext[Extensions.AppExt][AppExtensionKeys.expId] = expId;
        }
        if (typeof this.app.env === 'string') {
            event.ext[Extensions.AppExt][AppExtensionKeys.env] = this.app.env;
        }
    };
    TelemetryContext.prototype.applyUserContext = function (event) {
        var localId = this.user.localId;
        if (typeof localId === 'string') {
            event.ext[Extensions.UserExt][UserExtensionKeys.localId] = localId;
        }
        if (typeof this.user.locale === 'string') {
            event.ext[Extensions.UserExt][UserExtensionKeys.locale] = this.user.locale;
        }
    };
    TelemetryContext.prototype.applyWebContext = function (event) {
        if (typeof this.web.domain === 'string') {
            event.ext[Extensions.WebExt][WebExtensionKeys.domain] = this.web.domain;
        }
        if (typeof this.web.browser === 'string') {
            event.ext[Extensions.WebExt][WebExtensionKeys.browser] = this.web.browser;
        }
        if (typeof this.web.browserVer === 'string') {
            event.ext[Extensions.WebExt][WebExtensionKeys.browserVer] = this.web.browserVer;
        }
        if (typeof this.web.screenRes === 'string') {
            event.ext[Extensions.WebExt][WebExtensionKeys.screenRes] = this.web.screenRes;
        }
        event.ext[Extensions.WebExt][WebExtensionKeys.userConsent] = this.web.userConsent;
    };
    TelemetryContext.prototype.applyOsContext = function (event) {
        if (typeof this.os.name === 'string') {
            event.ext[Extensions.OSExt][OSExtKeys.osName] = this.os.name;
        }
        if (typeof this.os.ver === 'string') {
            event.ext[Extensions.OSExt][OSExtKeys.ver] = this.os.ver;
        }
    };
    TelemetryContext.prototype.applySdkContext = function (event) {
        event.ext[Extensions.SdkExt][SDKExtKeys.seq] = this.sdk.getSequenceId();
        event.ext[Extensions.SdkExt][SDKExtKeys.epoch] = this.sdk.epoch;
        if (typeof this.sdk.installId === 'string') {
            event.ext[Extensions.SdkExt][SDKExtKeys.installId] = this.sdk.installId;
        }
    };
    TelemetryContext.prototype.applyIntWebContext = function (event) {
        var msfpc = this.intWeb.msfpc;
        if (typeof msfpc === 'string') {
            event.ext[Extensions.IntWebExt][IntWebExtKeys.msfpc] = msfpc;
        }
        var anid = this.intWeb.anid;
        if (typeof anid === 'string') {
            event.ext[Extensions.IntWebExt][IntWebExtKeys.anid] = anid;
        }
        if (typeof this.intWeb.serviceName === 'string') {
            event.ext[Extensions.IntWebExt][IntWebExtKeys.serviceName] = this.intWeb.serviceName;
        }
    };
    TelemetryContext.prototype.applyUtcContext = function (event) {
        event.ext[Extensions.UtcExt][UtcExtKeys.popSample] = this.utc.popSample;
    };
    TelemetryContext.prototype.applyLocContext = function (event) {
        event.ext[Extensions.LocExt][LocExtKeys.tz] = this.loc.tz;
    };
    TelemetryContext.prototype.applySessionContext = function (event) {
        if (this.session) {
            if (typeof this.session.id !== 'string') {
                // Session was not specified, so create one
                this.sessionManager.update();
            }
        }
        var autoSession = this.sessionManager.automaticSession;
        if (autoSession && autoSession.id) {
            if (typeof autoSession.id === 'string') {
                event.ext[Extensions.AppExt][SessionExtKeys.sessionId] = autoSession.id;
            }
        }
        if (this.session && this.session.id) {
            // If customer set session info, apply that context; otherwise apply context automatically generated
            if (typeof this.session.id === 'string') {
                event.ext[Extensions.AppExt][SessionExtKeys.sessionId] = this.session.id;
            }
        }
    };
    TelemetryContext.prototype.applyDeviceContext = function (event) {
        if (typeof this.device.localId === 'string') {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.localId] = this.device.localId;
        }
        if (typeof this.device.make === 'string') {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.make] = this.device.make;
        }
        if (typeof this.device.model === 'string') {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.model] = this.device.model;
        }
        if (typeof this.device.deviceClass === 'string') {
            event.ext[Extensions.DeviceExt][DeviceExtKeys.deviceClass] = this.device.deviceClass;
        }
    };
    return TelemetryContext;
}());
export { TelemetryContext };
//# sourceMappingURL=TelemetryContext.js.map